import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone, X } from "lucide-react";
import { site } from "@/config/site";
import { useLeadForm } from "@/components/lead/LeadForm";
import { trackEvent } from "@/lib/tracking";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";

const nav = [
  { label: "Home", to: "/" },
  { label: "Blinds", to: "/blinds" },
  { label: "Shutters", to: "/shutters" },
  { label: "Why Choose Us", to: "/why-choose-us" },
  { label: "Our Work", to: "/our-work" },
  { label: "Areas We Cover", to: "/areas-we-cover" },
  { label: "About Us", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export function Header() {
  const { openForm } = useLeadForm();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <>
      <div className="hidden md:block bg-navy-900 text-white/90">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-center gap-8 py-1.5 text-[11px] tracking-[0.14em] uppercase">
          {site.trustClaims.map((c) => (
            <span key={c} className="flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-white/50" />
              {c}
            </span>
          ))}
        </div>
      </div>
      <header className="sticky top-0 z-50 border-b border-warm-200/70 bg-warm-50/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-[76px]">
            <Link
              to="/"
              data-testid="header-logo"
              className="flex items-center gap-3 shrink-0"
              aria-label="My Blinds Company — home"
            >
              <img
                src="/logo.png"
                alt="My Blinds Company logo"
                className="h-10 lg:h-12 w-auto rounded"
              />
            </Link>
            <nav className="hidden xl:flex items-center gap-6" aria-label="Main navigation">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={({ isActive }) =>
                    `text-[13px] font-medium tracking-wide transition-colors duration-200 ${
                      isActive
                        ? "text-navy-900 border-b-2 border-navy-900 pb-1"
                        : "text-slate-600 hover:text-navy-900"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
            <div className="flex items-center gap-3">
              <a
                href={site.phone.href}
                data-testid="header-phone-link"
                onClick={() => trackEvent("phone_click", { location: "header" })}
                className="hidden lg:flex items-center gap-2 text-sm font-semibold text-navy-900 hover:text-navy-800 transition-colors"
              >
                <Phone className="h-4 w-4" />
                {site.phone.display}
              </a>
              <button
                type="button"
                data-testid="header-book-visit-btn"
                onClick={() => openForm({ source: "header" })}
                className="hidden sm:inline-flex items-center rounded-full bg-navy-900 px-5 py-2.5 text-[13px] font-medium text-white shadow-lg shadow-navy-900/10 transition-all duration-300 hover:bg-navy-800 hover:-translate-y-0.5"
              >
                {site.primaryCTA}
              </button>
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <button
                    type="button"
                    data-testid="mobile-menu-button"
                    aria-label="Open menu"
                    className="xl:hidden inline-flex items-center justify-center h-11 w-11 rounded-full border border-warm-300 bg-white"
                  >
                    <Menu className="h-5 w-5 text-navy-900" />
                  </button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[320px] bg-warm-50 p-0">
                  <SheetTitle className="sr-only">Menu</SheetTitle>
                  <div className="flex flex-col h-full p-6">
                    <img src="/logo.png" alt="My Blinds Company logo" className="h-10 w-auto rounded self-start mb-8" />
                    <nav className="flex flex-col gap-1" aria-label="Mobile navigation">
                      {nav.map((n) => (
                        <Link
                          key={n.to}
                          to={n.to}
                          onClick={() => setOpen(false)}
                          data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className={`rounded-xl px-4 py-3 text-base font-medium transition-colors ${
                            location.pathname === n.to
                              ? "bg-navy-900 text-white"
                              : "text-slate-700 hover:bg-warm-100"
                          }`}
                        >
                          {n.label}
                        </Link>
                      ))}
                    </nav>
                    <div className="mt-auto space-y-3 pt-8">
                      <button
                        type="button"
                        data-testid="mobile-menu-book-btn"
                        onClick={() => {
                          setOpen(false);
                          openForm({ source: "mobile_menu" });
                        }}
                        className="w-full rounded-full bg-navy-900 py-3.5 text-sm font-medium text-white"
                      >
                        {site.primaryCTA}
                      </button>
                      <a
                        href={site.phone.href}
                        data-testid="mobile-menu-phone-link"
                        onClick={() => trackEvent("phone_click", { location: "mobile_menu" })}
                        className="flex w-full items-center justify-center gap-2 rounded-full border border-navy-900 py-3.5 text-sm font-medium text-navy-900"
                      >
                        <Phone className="h-4 w-4" /> {site.phone.display}
                      </a>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
